import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { api } from "../lib/api";
import { fmtINR, fmtPct, fmtNum, CR, L } from "../lib/format";
import KpiCard from "../components/KpiCard";
import Panel from "../components/Panel";
import {
    ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, ComposedChart,
    Bar, Line, LineChart, CartesianGrid, Cell, BarChart, Legend, RadialBarChart, RadialBar, PolarAngleAxis,
    Sankey, Rectangle, Layer
} from "recharts";
import { AlertTriangle, ArrowUpRight, TrendingUp, Zap, Target, Activity, Brain, Gauge, Sparkles, Clock, GitBranch, ShieldAlert, Filter, X, Sliders, BarChart3, DollarSign, ShieldCheck, Folder } from "lucide-react";
import ExportMenu from "../components/ExportMenu";

const AMBER = "#fb8b1e";
const POS = "#4af6c3";
const NEG = "#ff433d";
const NEU = "#0068ff";
const GRID = "#E2E8F0";
const TEXT_MUTED = "#8892B0";

const gradeColor = (g) => ({ "A+": POS, "A": POS, "B": AMBER, "C": "#ff8c42", "D": NEG }[g] || AMBER);

export default function Overview() {
    const ctx = useOutletContext() || {};
    const [data, setData] = useState(ctx.overview);
    const [receivables, setReceivables] = useState(null);
    const [payables, setPayables] = useState(null);
    const [alerts, setAlerts] = useState(null);
    const [briefing, setBriefing] = useState(null);
    const [scores, setScores] = useState(null);
    const [probForecast, setProbForecast] = useState(null);
    const [sankeyData, setSankeyData] = useState(null);
    const [anomalies, setAnomalies] = useState(null);

    // Tabs
    const [tab, setTab] = useState("executive");
    const TABS = [
        { key: "executive", label: "Executive Summary", icon: BarChart3 },
        { key: "cash", label: "Cash", icon: DollarSign },
        { key: "projects", label: "Projects", icon: Folder },
        { key: "compliance", label: "Compliance", icon: ShieldCheck },
    ];

    // Slicers
    const [slicerOpen, setSlicerOpen] = useState(false);
    const [slicer, setSlicer] = useState({ period: "fy", project_ids: [], client_ids: [], account_ids: [] });
    const [slicerOptions, setSlicerOptions] = useState({ projects: [], clients: [], accounts: [] });

    useEffect(() => {
        Promise.all([
            api.get("/overview").then((r) => setData(r.data)),
            api.get("/receivables").then((r) => setReceivables(r.data)),
            api.get("/payables").then((r) => setPayables(r.data)),
            api.get("/risk/alerts").then((r) => setAlerts(r.data)),
            api.get("/intelligence/briefing").then((r) => setBriefing(r.data)),
            api.get("/intelligence/scores").then((r) => setScores(r.data)),
            api.get("/cashflow/forecast-probabilistic").then((r) => setProbForecast(r.data)),
            api.get("/analytics/sankey").then((r) => setSankeyData(r.data)).catch(() => {}),
            api.get("/analytics/anomalies").then((r) => setAnomalies(r.data)).catch(() => {}),
        ]).catch(() => {});
        Promise.all([
            api.get("/data/projects").then((r) => r.data.items || []).catch(() => []),
            api.get("/data/clients").then((r) => r.data.items || []).catch(() => []),
            api.get("/bank-import/accounts-summary").then((r) => r.data.accounts || []).catch(() => []),
        ]).then(([projects, clients, accounts]) => setSlicerOptions({ projects, clients, accounts }));
    }, []);

    const activeFilterCount = (slicer.project_ids?.length || 0) + (slicer.client_ids?.length || 0) + (slicer.account_ids?.length || 0) + (slicer.period !== "fy" ? 1 : 0);
    const clearAllFilters = () => setSlicer({ period: "fy", project_ids: [], client_ids: [], account_ids: [] });

    const filteredAlerts = useMemo(() => {
        if (!alerts?.alerts) return [];
        if (slicer.project_ids.length === 0 && slicer.client_ids.length === 0) return alerts.alerts;
        return alerts.alerts.filter((a) => {
            const desc = (a.description || "").toLowerCase();
            const proj = (a.project || "").toLowerCase();
            const matchesProj = slicer.project_ids.length === 0 || slicer.project_ids.some((pid) => {
                const p = slicerOptions.projects.find((x) => x.id === pid);
                return p && (desc.includes((p.name || "").toLowerCase()) || proj.includes((p.id || "").toLowerCase()));
            });
            const matchesClient = slicer.client_ids.length === 0 || slicer.client_ids.some((cid) => {
                const c = slicerOptions.clients.find((x) => x.id === cid);
                return c && desc.includes((c.name || "").toLowerCase());
            });
            return matchesProj && matchesClient;
        });
    }, [alerts, slicer, slicerOptions]);

    if (!data) return <div className="text-[var(--text-secondary)] text-sm p-4">Loading Command Center…</div>;

    const k = data.kpis;
    const targetProgress = (k.turnover_ytd / k.turnover_target_annual) * 100;
    const target100Progress = (k.turnover_ytd / k.turnover_target_18m) * 100;

    const monthly = (data.monthly_series || []).map((m) => ({
        ...m,
        revenue_l: m.revenue / L,
        cost_l: m.cost / L,
        cashflow_l: m.net_cashflow / L,
    }));

    const forecast = (probForecast?.forecast || []).map((w) => ({
        week: w.week,
        p10: w.p10 / L,
        p50: w.p50 / L,
        p90: w.p90 / L,
        inflow: w.inflow / L,
        outflow: -w.outflow / L,
    }));

    const arAgeing = receivables ? Object.entries(receivables.ageing).map(([k, v]) => ({ bucket: k, value: v / L })) : [];
    const apAgeing = payables ? Object.entries(payables.ageing).map(([k, v]) => ({ bucket: k, value: v / L })) : [];

    const gaugeData = scores ? [{ name: "Health", value: scores.company_health.score, fill: gradeColor(scores.company_health.grade) }] : [];

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// CFO COMMAND CENTER</div>
                    <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">Company Pulse</h1>
                    <div className="mt-1 font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider">
                        A W Constructions Pvt Ltd · CIN {data.company.cin} · GSTIN {data.company.gstin}
                    </div>
                </div>
                <div className="flex items-center gap-2 text-[10px] flex-wrap">
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 border border-panel rounded">
                        <span className="w-1.5 h-1.5 bg-[var(--pos)] blink rounded-full"></span> LIVE
                    </span>
                    <span className="px-2 py-1 border border-panel text-[var(--text-secondary)] rounded">FY 25-26</span>
                    <Link to="/simulator" data-testid="quick-sim-link" className="px-3 py-1 border border-panel hover:border-[var(--pbi-blue)] flex items-center gap-1 rounded">
                        <Gauge size={11} /> WHAT-IF
                    </Link>
                    <Link to="/intelligence" data-testid="quick-intel-link" className="px-3 py-1 border border-panel hover:border-[var(--pbi-blue)] flex items-center gap-1 rounded">
                        <Brain size={11} /> INTELLIGENCE
                    </Link>
                    <Link to="/ai-advisor" data-testid="quick-ai-link" className="px-3 py-1 bg-[var(--brand-amber)] text-black font-semibold flex items-center gap-1 rounded">
                        <Zap size={11} /> ASK AI
                    </Link>
                    <button
                        data-testid="btn-open-slicer"
                        onClick={() => setSlicerOpen(true)}
                        className={`px-3 py-1 border flex items-center gap-1 rounded transition-colors ${activeFilterCount > 0 ? "border-[var(--pbi-blue)] text-[var(--pbi-blue)] bg-[var(--pbi-blue-dim)]" : "border-panel hover:border-[var(--pbi-blue)]"}`}
                    >
                        <Sliders size={11} /> Slicers {activeFilterCount > 0 && <span className="ml-1 bg-[var(--pbi-blue)] text-white text-[9px] px-1 rounded">{activeFilterCount}</span>}
                    </button>
                    <ExportMenu kind="boardpack" label="Board Pack" testId="export-boardpack" />
                </div>
            </div>

            {/* Report Tabs · Power BI style */}
            <div className="border-b border-panel bg-panel">
                <div className="flex items-center gap-0 px-2" data-testid="overview-tabs">
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        const active = tab === t.key;
                        return (
                            <button
                                key={t.key}
                                data-testid={`tab-${t.key}`}
                                onClick={() => setTab(t.key)}
                                className={`flex items-center gap-2 px-4 py-2.5 text-[13px] border-b-2 transition-all -mb-px ${
                                    active
                                        ? "border-[var(--pbi-blue)] text-[var(--text-primary)] font-semibold bg-[var(--pbi-blue-dim)]"
                                        : "border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-panel-hover)]"
                                }`}
                            >
                                <Icon size={14} /> {t.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Active Filter Chips */}
            {activeFilterCount > 0 && (
                <div className="flex items-center gap-2 flex-wrap p-2 border border-[var(--pbi-blue)] bg-[var(--pbi-blue-dim)] rounded text-[11px]" data-testid="active-filter-chips">
                    <Filter size={12} className="text-[var(--pbi-blue)]" />
                    <span className="text-[var(--pbi-blue)] font-semibold">Filters:</span>
                    {slicer.period !== "fy" && (
                        <span className="px-2 py-0.5 bg-white border border-[var(--pbi-blue)] rounded flex items-center gap-1">Period · {slicer.period.toUpperCase()}
                            <button onClick={() => setSlicer((s) => ({ ...s, period: "fy" }))}><X size={10} /></button>
                        </span>
                    )}
                    {slicer.project_ids.map((pid) => {
                        const p = slicerOptions.projects.find((x) => x.id === pid);
                        return (
                            <span key={pid} className="px-2 py-0.5 bg-white border border-[var(--pbi-blue)] rounded flex items-center gap-1" data-testid={`chip-project-${pid}`}>
                                Project · {p?.name || pid}
                                <button onClick={() => setSlicer((s) => ({ ...s, project_ids: s.project_ids.filter((x) => x !== pid) }))}><X size={10} /></button>
                            </span>
                        );
                    })}
                    {slicer.client_ids.map((cid) => {
                        const c = slicerOptions.clients.find((x) => x.id === cid);
                        return (
                            <span key={cid} className="px-2 py-0.5 bg-white border border-[var(--pbi-blue)] rounded flex items-center gap-1" data-testid={`chip-client-${cid}`}>
                                Client · {c?.name || cid}
                                <button onClick={() => setSlicer((s) => ({ ...s, client_ids: s.client_ids.filter((x) => x !== cid) }))}><X size={10} /></button>
                            </span>
                        );
                    })}
                    {slicer.account_ids.map((aid) => {
                        const a = slicerOptions.accounts.find((x) => x.id === aid);
                        return (
                            <span key={aid} className="px-2 py-0.5 bg-white border border-[var(--pbi-blue)] rounded flex items-center gap-1" data-testid={`chip-account-${aid}`}>
                                Account · {a?.bank_name || aid}
                                <button onClick={() => setSlicer((s) => ({ ...s, account_ids: s.account_ids.filter((x) => x !== aid) }))}><X size={10} /></button>
                            </span>
                        );
                    })}
                    <button data-testid="btn-clear-all-filters" onClick={clearAllFilters} className="ml-auto text-[var(--pbi-blue)] text-[10px] font-semibold hover:underline">Clear All</button>
                </div>
            )}

            {/* ============ EXECUTIVE SUMMARY TAB ============ */}
            {tab === "executive" && <>
            {/* Executive Briefing */}
            {briefing && (
                <Panel testId="briefing" title="Executive Briefing" subtitle={`${briefing.period} · Auto-generated`} right={
                    <div className="flex items-center gap-3">
                        <span className="text-[10px] font-mono-fin text-[var(--text-muted)] uppercase tracking-wider flex items-center gap-1"><Clock size={10} /> {briefing.next_review}</span>
                        <Link to="/ai-advisor" className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--brand-amber)] flex items-center gap-1"><Sparkles size={11} /> AI Elaborate</Link>
                    </div>
                }>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        <div>
                            <div className="text-[10px] font-mono-fin uppercase tracking-[0.15em] text-pos mb-2">✓ Wins ({briefing.wins.length})</div>
                            <ul className="space-y-1.5 text-xs text-[var(--text-primary)] font-body leading-relaxed">
                                {briefing.wins.slice(0, 3).map((w, i) => <li key={i} className="flex gap-2"><span className="text-pos shrink-0">▸</span><span>{w}</span></li>)}
                            </ul>
                        </div>
                        <div>
                            <div className="text-[10px] font-mono-fin uppercase tracking-[0.15em] text-neg mb-2">⚠ Concerns ({briefing.concerns.length})</div>
                            <ul className="space-y-1.5 text-xs text-[var(--text-primary)] font-body leading-relaxed">
                                {briefing.concerns.slice(0, 3).map((c, i) => <li key={i} className="flex gap-2"><span className="text-neg shrink-0">▸</span><span>{c}</span></li>)}
                            </ul>
                        </div>
                        <div>
                            <div className="text-[10px] font-mono-fin uppercase tracking-[0.15em] text-amber mb-2">◈ Bold Move Today</div>
                            <div className="border border-[var(--brand-amber)] p-3 bg-[var(--brand-amber-dim)]">
                                <div className="text-xs text-[var(--text-primary)] font-body leading-relaxed">{briefing.bold_move}</div>
                            </div>
                        </div>
                    </div>
                </Panel>
            )}

            {/* Health Score + Cash Runway + Distress row */}
            {scores && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Panel testId="ov-health" title="Company Health" subtitle="Composite score">
                        <div className="flex items-center gap-4">
                            <div className="w-24 h-24 shrink-0 relative">
                                <ResponsiveContainer width="100%" height="100%">
                                    <RadialBarChart cx="50%" cy="50%" innerRadius="72%" outerRadius="100%" data={gaugeData} startAngle={225} endAngle={-45}>
                                        <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                                        <RadialBar dataKey="value" cornerRadius={0} background={{ fill: "#E2E8F0" }} />
                                    </RadialBarChart>
                                </ResponsiveContainer>
                                <div className="absolute inset-0 flex items-center justify-center flex-col">
                                    <span className="font-mono-fin text-2xl font-bold" style={{ color: gradeColor(scores.company_health.grade) }}>{scores.company_health.score}</span>
                                </div>
                            </div>
                            <div className="flex-1">
                                <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">Grade</div>
                                <div className="font-display font-black text-2xl" style={{ color: gradeColor(scores.company_health.grade) }}>{scores.company_health.grade}</div>
                                <Link to="/intelligence" className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--brand-amber)] hover:underline">Breakdown →</Link>
                            </div>
                        </div>
                    </Panel>

                    <Panel testId="ov-runway" title="Cash Runway" subtitle="At current burn rate">
                        <div className="flex items-baseline gap-3">
                            <span className="font-mono-fin text-4xl font-bold" style={{ color: scores.cash_runway.status === "Critical" ? NEG : scores.cash_runway.status === "Watchlist" ? AMBER : POS }}>{scores.cash_runway.days}</span>
                            <span className="text-xs font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">days</span>
                        </div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider mt-1" style={{ color: scores.cash_runway.status === "Critical" ? NEG : scores.cash_runway.status === "Watchlist" ? AMBER : POS }}>● {scores.cash_runway.status}</div>
                        <div className="mt-3 text-[10px] font-mono-fin text-[var(--text-secondary)] uppercase tracking-wider space-y-1">
                            <div>Cash: <span className="text-[var(--text-primary)]">{fmtINR(scores.cash_runway.cash)}</span></div>
                            <div>Burn: <span className="text-neg">{fmtINR(scores.cash_runway.monthly_burn)}/mo</span></div>
                        </div>
                    </Panel>

                    <Panel testId="ov-distress" title="Financial Distress" subtitle="Altman Z-Score">
                        <div className="flex items-baseline gap-3">
                            <span className="font-mono-fin text-4xl font-bold" style={{ color: scores.distress.zone === "Safe" ? POS : scores.distress.zone === "Grey" ? AMBER : NEG }}>{scores.distress.altman_z.toFixed(2)}</span>
                            <span className="text-xs font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">{scores.distress.zone}</span>
                        </div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider mt-1 text-amber">◆ {scores.distress.flag}</div>
                        <div className="mt-3 text-[10px] font-mono-fin text-[var(--text-secondary)] uppercase tracking-wider space-y-1">
                            <div>Beneish M: <span className="text-[var(--text-primary)]">{scores.distress.beneish_m}</span></div>
                            <div>Bands: <span className="text-[var(--text-primary)]">2.99 · 1.81</span></div>
                        </div>
                    </Panel>
                </div>
            )}

            {/* Top KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <KpiCard testId="kpi-turnover" label="Turnover YTD" value={k.turnover_ytd} sub={`Target ₹${(k.turnover_target_annual/CR).toFixed(1)} Cr`} delta={9.4} deltaLabel="YoY" accent />
                <KpiCard testId="kpi-cash" label="Cash & Bank" value={k.cash_bank} sub="4 accounts" delta={-3.2} deltaLabel="MoM" />
                <KpiCard testId="kpi-debt" label="Outstanding Debt" value={k.total_debt} sub="4 lenders" delta={-4.1} deltaLabel="MoM" />
                <KpiCard testId="kpi-ar" label="Receivables" value={k.receivables_total} sub={`Overdue ₹${(k.receivables_overdue/L).toFixed(1)} L`} delta={-2.1} deltaLabel="WoW" />
                <KpiCard testId="kpi-ap" label="Payables" value={k.payables_total} sub={`Overdue ₹${(k.payables_overdue/L).toFixed(1)} L`} delta={5.4} deltaLabel="WoW" />
                <KpiCard testId="kpi-orderbook" label="Order Book" value={k.order_book} sub="Executable pipeline" delta={12.8} deltaLabel="MoM" />
            </div>

            {/* Target progress */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <Panel testId="panel-annual-target" title="FY 25-26 Turnover Progress" right={<span className="font-mono-fin text-xs text-[var(--brand-amber)]">{targetProgress.toFixed(1)}%</span>}>
                    <div className="space-y-2">
                        <div className="flex justify-between text-[11px] font-mono-fin uppercase tracking-wider">
                            <span className="text-[var(--text-secondary)]">YTD ₹{(k.turnover_ytd/CR).toFixed(2)} Cr</span>
                            <span className="text-[var(--text-primary)]">Target ₹{(k.turnover_target_annual/CR).toFixed(1)} Cr</span>
                        </div>
                        <div className="h-3 bg-[var(--bg-base)] border border-panel relative overflow-hidden">
                            <div className="h-full bg-[var(--brand-amber)] transition-all" style={{ width: `${Math.min(100, targetProgress)}%` }} />
                        </div>
                        <div className="text-[10px] font-mono-fin text-[var(--text-muted)]">Gap to close: ₹{((k.turnover_target_annual - k.turnover_ytd)/CR).toFixed(2)} Cr in 6 weeks · Q4 must-do</div>
                    </div>
                </Panel>
                <Panel testId="panel-18m-target" title="₹100 Cr Vision · 18-Month Ambition" right={<span className="font-mono-fin text-xs text-[var(--brand-amber)]">{target100Progress.toFixed(1)}%</span>}>
                    <div className="space-y-2">
                        <div className="flex justify-between text-[11px] font-mono-fin uppercase tracking-wider">
                            <span className="text-[var(--text-secondary)]">Current ₹{(k.turnover_ytd/CR).toFixed(2)} Cr</span>
                            <span className="text-[var(--brand-amber)]">Target ₹100 Cr</span>
                        </div>
                        <div className="h-3 bg-[var(--bg-base)] border border-panel relative overflow-hidden diag-stripe">
                            <div className="h-full bg-gradient-to-r from-[var(--brand-amber)] to-[#ff433d] transition-all" style={{ width: `${Math.min(100, target100Progress)}%` }} />
                        </div>
                        <div className="text-[10px] font-mono-fin text-[var(--text-muted)]">Needs 6.7x growth · <Link to="/simulator" className="text-[var(--brand-amber)] underline">Simulate path →</Link></div>
                    </div>
                </Panel>
            </div>

            {/* Revenue + margins */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
                <Panel testId="panel-revenue-trend" className="xl:col-span-2" title="Revenue · Cost · Gross Profit" subtitle="12-Month Trend (₹ Lakhs)">
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={monthly}>
                                <CartesianGrid stroke={GRID} strokeOpacity={0.4} vertical={false} />
                                <XAxis dataKey="month" tick={{ fill: TEXT_MUTED, fontSize: 10 }} axisLine={{ stroke: GRID }} tickLine={false} />
                                <YAxis tick={{ fill: TEXT_MUTED, fontSize: 10 }} axisLine={{ stroke: GRID }} tickLine={false} unit="L" />
                                <Tooltip cursor={{ fill: "rgba(251,139,30,0.08)" }} />
                                <Legend />
                                <Bar dataKey="revenue_l" name="Revenue" fill={AMBER} maxBarSize={22} />
                                <Bar dataKey="cost_l" name="Cost" fill={NEG} maxBarSize={22} fillOpacity={0.55} />
                                <Line dataKey="cashflow_l" name="Net Cash Flow" stroke={POS} strokeWidth={2} dot={{ fill: POS, r: 3 }} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                </Panel>

                <Panel testId="panel-margins" title="Margins" subtitle="YTD">
                    <div className="space-y-4">
                        {[
                            { label: "Gross Margin", value: k.gross_margin, target: 18.0, color: POS },
                            { label: "EBITDA Margin", value: k.ebitda_margin, target: 12.0, color: AMBER },
                            { label: "Net Profit Margin", value: k.net_profit_margin, target: 6.0, color: NEU },
                        ].map((m) => (
                            <div key={m.label}>
                                <div className="flex justify-between text-[11px] font-mono-fin uppercase tracking-wider mb-1.5">
                                    <span className="text-[var(--text-secondary)]">{m.label}</span>
                                    <span style={{ color: m.color }} className="font-medium">{m.value.toFixed(1)}%</span>
                                </div>
                                <div className="h-2 bg-[var(--bg-base)] border border-panel relative">
                                    <div className="h-full" style={{ width: `${Math.min(100, (m.value / m.target) * 100)}%`, background: m.color }} />
                                    <div className="absolute top-0 h-full border-l border-[var(--text-primary)]/30" style={{ left: "100%" }} />
                                </div>
                                <div className="text-[9px] font-mono-fin text-[var(--text-muted)] mt-1 uppercase tracking-wider">Benchmark {m.target.toFixed(1)}%</div>
                            </div>
                        ))}
                        <div className="border-t border-panel pt-3 mt-2">
                            <div className="text-[10px] font-mono-fin uppercase tracking-[0.15em] text-[var(--text-secondary)] mb-1">Cash Conversion Cycle</div>
                            <div className="font-mono-fin text-2xl text-[var(--text-primary)]">68 <span className="text-sm text-[var(--text-secondary)]">days</span></div>
                            <div className="text-[10px] font-mono-fin text-[var(--text-muted)] mt-1">Industry: 60d · 8 days worse</div>
                        </div>
                    </div>
                </Panel>
            </div>

            {/* Probabilistic Cash Flow */}
            </>}

            {/* ============ CASH TAB ============ */}
            {tab === "cash" && <>
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
                <Panel testId="prob-cashflow" className="xl:col-span-2" title="90-Day Cash Flow Forecast" subtitle={probForecast?.commentary || "P10/P50/P90 confidence bands (₹ Lakhs)"}>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={forecast}>
                                <defs>
                                    <linearGradient id="p90" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={POS} stopOpacity={0.35} />
                                        <stop offset="100%" stopColor={POS} stopOpacity={0.05} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid stroke={GRID} strokeOpacity={0.4} vertical={false} />
                                <XAxis dataKey="week" tick={{ fill: TEXT_MUTED, fontSize: 9 }} axisLine={{ stroke: GRID }} tickLine={false} />
                                <YAxis tick={{ fill: TEXT_MUTED, fontSize: 10 }} axisLine={{ stroke: GRID }} tickLine={false} unit="L" />
                                <Tooltip formatter={(v, n) => [`₹${v.toFixed(1)} L`, n.toUpperCase()]} />
                                <Legend />
                                <Area type="monotone" dataKey="p90" name="P90 Best-case" stroke={POS} strokeOpacity={0.4} fill="url(#p90)" />
                                <Area type="monotone" dataKey="p10" name="P10 Worst-case" stroke={NEG} strokeOpacity={0.4} fill={NEG} fillOpacity={0.08} />
                                <Line dataKey="p50" name="P50 Base-case" stroke={AMBER} strokeWidth={2.5} dot={{ fill: AMBER, r: 3 }} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                </Panel>

                <Panel testId="panel-ageing" title="AR · AP Ageing" subtitle="₹ Lakhs">
                    <div className="space-y-4">
                        <div>
                            <div className="text-[10px] font-mono-fin uppercase tracking-[0.15em] text-[var(--pos)] mb-2">Receivables ({fmtINR(receivables?.total || 0)})</div>
                            <div className="h-20">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={arAgeing}>
                                        <XAxis dataKey="bucket" tick={{ fill: TEXT_MUTED, fontSize: 9 }} axisLine={{ stroke: GRID }} tickLine={false} />
                                        <YAxis hide />
                                        <Tooltip />
                                        <Bar dataKey="value" name="₹ L">
                                            {arAgeing.map((d, i) => (
                                                <Cell key={i} fill={d.bucket === "0-30" ? POS : d.bucket === "31-60" ? AMBER : NEG} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] font-mono-fin uppercase tracking-[0.15em] text-[var(--neg)] mb-2">Payables ({fmtINR(payables?.total || 0)})</div>
                            <div className="h-20">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={apAgeing}>
                                        <XAxis dataKey="bucket" tick={{ fill: TEXT_MUTED, fontSize: 9 }} axisLine={{ stroke: GRID }} tickLine={false} />
                                        <YAxis hide />
                                        <Tooltip />
                                        <Bar dataKey="value" name="₹ L" fill={NEG} fillOpacity={0.6} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                </Panel>
            </div>

            {/* Sankey Cash-Flow + Anomaly Watch */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
                <Panel testId="panel-sankey" className="xl:col-span-2" title="Cash Flow · Sources → Hub → Uses" subtitle={sankeyData?.summary?.total_inflow > 0 ? `${sankeyData?.summary?.sources} sources · ${sankeyData?.summary?.uses} uses · Net ${fmtINR(sankeyData?.summary?.net || 0)}` : "Post real receipts, POs and payments to see the flow"} right={<Link to="/analytics" className="text-[10px] text-[var(--pbi-blue)]">Analytics Lab →</Link>}>
                    {sankeyData?.links?.length > 0 ? (
                        <div className="h-72">
                            <ResponsiveContainer width="100%" height="100%">
                                <Sankey
                                    data={{
                                        nodes: sankeyData.nodes.map((n) => ({ ...n })),
                                        links: sankeyData.links,
                                    }}
                                    nodePadding={16}
                                    node={<SankeyOverviewNode />}
                                    link={{ stroke: "#118DFF", strokeOpacity: 0.15 }}
                                    margin={{ left: 8, right: 8, top: 8, bottom: 8 }}
                                >
                                    <Tooltip formatter={(v) => fmtINR(v)} />
                                </Sankey>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <div className="h-56 flex items-center justify-center flex-col gap-2">
                            <GitBranch size={28} className="text-[var(--text-muted)]" />
                            <div className="text-xs text-[var(--text-secondary)] max-w-md text-center">
                                Once you post receipts (client → cash) and payments (cash → vendor), this Sankey shows the entire money flow at a glance.
                            </div>
                        </div>
                    )}
                </Panel>
                {/* Cash-specific side panel */}
                <Panel testId="cash-side" title="Cash Metrics" subtitle="At a glance">
                    <div className="space-y-3 text-sm">
                        <div>
                            <div className="text-[10px] uppercase text-[var(--text-secondary)]">Cash on Hand</div>
                            <div className="text-2xl font-semibold font-mono-fin text-pos">{fmtINR(k.cash_bank)}</div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase text-[var(--text-secondary)]">Monthly Burn</div>
                            <div className="text-lg font-semibold font-mono-fin text-neg">{scores?.cash_runway ? fmtINR(scores.cash_runway.monthly_burn) : "—"}</div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase text-[var(--text-secondary)]">Runway</div>
                            <div className="text-lg font-semibold font-mono-fin">{scores?.cash_runway?.days || "—"} days</div>
                        </div>
                        <div className="border-t border-panel pt-3 mt-2 text-[11px] text-[var(--text-muted)]">
                            Slicer applies to alerts and outer KPIs. Full period-based cash filtering coming soon.
                        </div>
                    </div>
                </Panel>
            </div>
            </>}

            {/* ============ PROJECTS TAB ============ */}
            {tab === "projects" && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <KpiCard testId="kpi-active-projects" label="Active Projects" value={k.active_projects} format="raw" sub="Currently executing" />
                    <KpiCard testId="kpi-at-risk" label="At Risk" value={k.at_risk_projects} format="raw" sub="Cost overrun / delay" accent />
                    <KpiCard testId="kpi-order-book" label="Order Book" value={k.order_book} sub="Executable pipeline" delta={12.8} deltaLabel="MoM" />
                    <Panel testId="projects-portfolio-link" title="Project Portfolio" subtitle="Full project list, health, gross margins" className="md:col-span-3">
                        <div className="p-4 flex items-center justify-between">
                            <div className="text-sm text-[var(--text-secondary)]">Head over to the dedicated Client Portfolio and Project Billing modules for deep dives, cost-to-complete forecasts and health cards.</div>
                            <div className="flex gap-2">
                                <Link to="/client-portfolio" className="px-3 py-1.5 bg-[var(--pbi-blue)] text-white text-xs font-semibold rounded">Client Portfolio →</Link>
                                <Link to="/project-billing" className="px-3 py-1.5 border border-panel text-xs rounded hover:border-[var(--pbi-blue)]">Project Billing →</Link>
                                <Link to="/projects" className="px-3 py-1.5 border border-panel text-xs rounded hover:border-[var(--pbi-blue)]">All Projects →</Link>
                            </div>
                        </div>
                    </Panel>
                </div>
            )}

            {/* ============ COMPLIANCE TAB ============ */}
            {tab === "compliance" && <>
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
                <Panel testId="panel-anomaly" className="xl:col-span-2" title="Anomaly Watch" subtitle={anomalies ? `${anomalies?.summary?.flagged_txns || 0} flags · At risk ${fmtINR(anomalies?.summary?.total_at_risk_estimate || 0)}` : "Loading…"} right={<Link to="/analytics" className="text-[10px] text-[var(--pbi-blue)]">Details →</Link>}>
                    <div className="space-y-2">
                        {anomalies ? (
                            <>
                                <AnomalyRow icon={AlertTriangle} tone="neg" label="Duplicate suspects" count={anomalies.duplicates?.count || 0} value={anomalies.duplicates?.total_value || 0} />
                                <AnomalyRow icon={ShieldAlert} tone="warn" label="Statistical outliers (z>2)" count={anomalies.z_outliers?.count || 0} value={anomalies.z_outliers?.total_value || 0} />
                                <AnomalyRow icon={Target} tone="warn" label="Exact-lakh amounts" count={anomalies.round_amounts?.count || 0} value={anomalies.round_amounts?.total_value || 0} />
                                <AnomalyRow icon={TrendingUp} tone="neg" label="Over-billed projects" count={anomalies.over_billing?.count || 0} value={anomalies.over_billing?.total_value || 0} />
                                <AnomalyRow icon={Clock} tone="warn" label="Weekend transactions" count={anomalies.weekend_txns?.count || 0} value={anomalies.weekend_txns?.total_value || 0} />
                                <div className="mt-3 p-2 border border-[var(--border)] rounded">
                                    <div className="text-[10px] uppercase text-[var(--text-secondary)]">Benford Chi²</div>
                                    <div className="flex items-baseline gap-2">
                                        <span className="text-lg font-semibold">{anomalies.benford_first_digit?.chi_squared ?? "—"}</span>
                                        <span className={`text-[10px] uppercase ${
                                            anomalies.benford_first_digit?.verdict === "Suspicious" ? "text-[var(--neg)]" :
                                            anomalies.benford_first_digit?.verdict === "Acceptable" ? "text-[var(--brand-amber)]" :
                                            "text-[var(--pos)]"
                                        }`}>
                                            {anomalies.benford_first_digit?.verdict}
                                        </span>
                                    </div>
                                    <div className="text-[10px] text-[var(--text-secondary)] mt-0.5">Sample size {anomalies.benford_first_digit?.sample_size || 0}</div>
                                </div>
                            </>
                        ) : (
                            <div className="text-xs text-[var(--text-secondary)] py-4">Running anomaly checks…</div>
                        )}
                    </div>
                </Panel>

                <Panel testId="panel-alerts-compliance" title="Priority Alerts" subtitle={`${filteredAlerts.length} shown · ${alerts?.summary.by_severity.High || 0} high severity`} right={<Link to="/risk" className="text-[10px] text-[var(--pbi-blue)]">View all →</Link>}>
                    <div className="divide-y divide-[var(--border)]">
                        {(filteredAlerts).slice(0, 8).map((a) => (
                            <div key={a.id} className="py-2.5 flex items-start gap-3">
                                <AlertTriangle size={14} className={a.severity === "High" ? "text-[var(--neg)]" : a.severity === "Medium" ? "text-[var(--brand-amber)]" : "text-[var(--text-secondary)]"} />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className={`text-[9px] uppercase px-1.5 py-0.5 border rounded ${a.severity === "High" ? "border-[var(--neg)] text-[var(--neg)]" : a.severity === "Medium" ? "border-[var(--brand-amber)] text-[var(--brand-amber)]" : "border-panel text-[var(--text-secondary)]"}`}>{a.severity}</span>
                                        <span className="text-[10px] uppercase text-[var(--text-secondary)]">{a.type}</span>
                                    </div>
                                    <div className="text-xs text-[var(--text-primary)] mt-1 leading-snug">{a.description}</div>
                                </div>
                                <div className="text-xs text-[var(--brand-amber)] shrink-0 font-mono-fin">{fmtINR(a.amount)}</div>
                            </div>
                        ))}
                        {filteredAlerts.length === 0 && (
                            <div className="py-6 text-center text-xs text-[var(--text-secondary)]">
                                No alerts match the current slicer selection.
                            </div>
                        )}
                    </div>
                </Panel>
            </div>
            </>}

            {/* Slicer Drawer */}
            <SlicerDrawer
                open={slicerOpen}
                onClose={() => setSlicerOpen(false)}
                slicer={slicer}
                setSlicer={setSlicer}
                options={slicerOptions}
                onClear={clearAllFilters}
            />

            {/* HIDDEN — legacy sections replaced by tabs above */}
            <div style={{ display: "none" }}>
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
                <Panel testId="panel-sankey" className="xl:col-span-2" title="Cash Flow · Sources → Hub → Uses" subtitle={sankeyData?.summary?.total_inflow > 0 ? `${sankeyData?.summary?.sources} sources · ${sankeyData?.summary?.uses} uses · Net ${fmtINR(sankeyData?.summary?.net || 0)}` : "Post real receipts, POs and payments to see the flow"} right={<Link to="/analytics" className="font-mono-fin text-[10px] text-[var(--brand-amber)] uppercase tracking-wider">Analytics Lab →</Link>}>
                    {sankeyData?.links?.length > 0 ? (
                        <div className="h-72">
                            <ResponsiveContainer width="100%" height="100%">
                                <Sankey
                                    data={{
                                        nodes: sankeyData.nodes.map((n) => ({ ...n })),
                                        links: sankeyData.links,
                                    }}
                                    nodePadding={16}
                                    node={<SankeyOverviewNode />}
                                    link={{ stroke: "#fb8b1e", strokeOpacity: 0.15 }}
                                    margin={{ left: 8, right: 8, top: 8, bottom: 8 }}
                                >
                                    <Tooltip formatter={(v) => fmtINR(v)} />
                                </Sankey>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <div className="h-56 flex items-center justify-center flex-col gap-2">
                            <GitBranch size={28} className="text-[var(--text-muted)]" />
                            <div className="text-xs font-mono-fin text-[var(--text-secondary)] max-w-md text-center">
                                Once you post receipts (client → cash) and payments (cash → vendor), this Sankey shows the entire money flow at a glance.
                            </div>
                        </div>
                    )}
                </Panel>

                <Panel testId="panel-anomaly" title="Anomaly Watch" subtitle={anomalies ? `${anomalies?.summary?.flagged_txns || 0} flags · At risk ${fmtINR(anomalies?.summary?.total_at_risk_estimate || 0)}` : "Loading…"} right={<Link to="/analytics" className="font-mono-fin text-[10px] text-[var(--brand-amber)] uppercase tracking-wider">Details →</Link>}>
                    <div className="space-y-2">
                        {anomalies ? (
                            <>
                                <AnomalyRow icon={AlertTriangle} tone="neg" label="Duplicate suspects" count={anomalies.duplicates?.count || 0} value={anomalies.duplicates?.total_value || 0} />
                                <AnomalyRow icon={ShieldAlert} tone="warn" label="Statistical outliers (z>2)" count={anomalies.z_outliers?.count || 0} value={anomalies.z_outliers?.total_value || 0} />
                                <AnomalyRow icon={Target} tone="warn" label="Exact-lakh amounts" count={anomalies.round_amounts?.count || 0} value={anomalies.round_amounts?.total_value || 0} />
                                <AnomalyRow icon={TrendingUp} tone="neg" label="Over-billed projects" count={anomalies.over_billing?.count || 0} value={anomalies.over_billing?.total_value || 0} />
                                <AnomalyRow icon={Clock} tone="warn" label="Weekend transactions" count={anomalies.weekend_txns?.count || 0} value={anomalies.weekend_txns?.total_value || 0} />
                                <div className="mt-3 p-2 border border-[var(--border)]">
                                    <div className="text-[10px] font-mono-fin uppercase text-[var(--text-secondary)] tracking-wider">Benford Chi²</div>
                                    <div className="flex items-baseline gap-2">
                                        <span className="text-lg font-bold">{anomalies.benford_first_digit?.chi_squared ?? "—"}</span>
                                        <span className={`text-[10px] font-mono-fin uppercase ${
                                            anomalies.benford_first_digit?.verdict === "Suspicious" ? "text-[var(--neg)]" :
                                            anomalies.benford_first_digit?.verdict === "Acceptable" ? "text-[var(--brand-amber)]" :
                                            "text-[var(--pos)]"
                                        }`}>
                                            {anomalies.benford_first_digit?.verdict}
                                        </span>
                                    </div>
                                    <div className="text-[10px] text-[var(--text-secondary)] mt-0.5">
                                        Sample size {anomalies.benford_first_digit?.sample_size || 0}
                                    </div>
                                </div>
                                {anomalies.summary?.note && (
                                    <div className="text-[10px] font-mono-fin text-[var(--text-muted)] italic mt-2">
                                        {anomalies.summary.note}
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="text-xs font-mono-fin text-[var(--text-secondary)] py-4">Running anomaly checks…</div>
                        )}
                    </div>
                </Panel>
            </div>

            {/* Alerts + Quick actions */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
                <Panel testId="panel-alerts" className="xl:col-span-2" title="Priority Alerts" subtitle={`${alerts?.summary.count || 0} open · ${alerts?.summary.by_severity.High || 0} high severity`} right={<Link to="/risk" className="font-mono-fin text-[10px] text-[var(--brand-amber)] uppercase tracking-wider">View all →</Link>}>
                    <div className="divide-y divide-[#E2E8F0]">
                        {(alerts?.alerts || []).slice(0, 5).map((a) => (
                            <div key={a.id} className="py-2.5 flex items-start gap-3">
                                <AlertTriangle size={14} className={a.severity === "High" ? "text-[var(--neg)]" : a.severity === "Medium" ? "text-[var(--brand-amber)]" : "text-[var(--text-secondary)]"} />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className={`text-[9px] font-mono-fin uppercase tracking-wider px-1.5 py-0.5 border ${a.severity === "High" ? "border-[var(--neg)] text-[var(--neg)]" : a.severity === "Medium" ? "border-[var(--brand-amber)] text-[var(--brand-amber)]" : "border-panel text-[var(--text-secondary)]"}`}>{a.severity}</span>
                                        <span className="text-[10px] font-mono-fin uppercase text-[var(--text-secondary)] tracking-wider">{a.type}</span>
                                    </div>
                                    <div className="text-xs text-[var(--text-primary)] mt-1 leading-snug">{a.description}</div>
                                </div>
                                <div className="font-mono-fin text-xs text-[var(--brand-amber)] shrink-0">{fmtINR(a.amount)}</div>
                            </div>
                        ))}
                    </div>
                </Panel>

                <Panel testId="panel-quick-nav" title="Fast Actions" subtitle="Jump to detail">
                    <div className="grid grid-cols-2 gap-2">
                        {[
                            { to: "/simulator", label: "What-If", icon: Gauge, count: "8Q model", color: "amber" },
                            { to: "/intelligence", label: "Scores", icon: Brain, count: `Health ${scores?.company_health.score || "—"}`, color: "amber" },
                            { to: "/receivables", label: "Collect", icon: TrendingUp, count: fmtINR(k.receivables_overdue), color: "pos" },
                            { to: "/loans", label: "Debt", icon: Activity, count: fmtINR(k.total_debt), color: "neg" },
                        ].map((q) => {
                            const Icon = q.icon;
                            return (
                                <Link
                                    key={q.to}
                                    to={q.to}
                                    data-testid={`quick-drill-${q.label.toLowerCase().replace(/\s/g, "-").replace(/-/g, "")}`}
                                    className="border border-panel p-3 hover:border-[var(--brand-amber)] hover:bg-[var(--bg-panel-hover)] transition-all group"
                                >
                                    <Icon size={14} style={{ color: q.color === "amber" ? AMBER : q.color === "pos" ? POS : NEG }} className="mb-2" />
                                    <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">{q.label}</div>
                                    <div className="text-sm font-mono-fin text-[var(--text-primary)] mt-0.5">{q.count}</div>
                                </Link>
                            );
                        })}
                    </div>
                    <Link to="/ai-advisor" data-testid="ai-cfo-cta" className="mt-4 block border border-[var(--brand-amber)] p-3 hover:bg-[var(--brand-amber-dim)] transition-all">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--brand-amber)]">AI CFO Advisor</div>
                                <div className="text-xs text-[var(--text-primary)] mt-1">Get instant strategy · Claude 4.5</div>
                            </div>
                            <Zap size={16} className="text-[var(--brand-amber)]" />
                        </div>
                    </Link>
                    <div className="mt-2 text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-muted)] text-center">
                        Tip: Press <kbd className="border border-panel px-1 text-[var(--text-primary)]">⌘K</kbd> to search anywhere
                    </div>
                </Panel>
            </div>
            </div>{/* end hidden legacy */}
        </div>
    );
}

// ============ Slicer Drawer ============
function SlicerDrawer({ open, onClose, slicer, setSlicer, options, onClear }) {
    if (!open) return null;
    const toggleId = (key, id) => {
        setSlicer((s) => ({
            ...s,
            [key]: s[key].includes(id) ? s[key].filter((x) => x !== id) : [...s[key], id],
        }));
    };
    const PERIODS = [
        { key: "fy", label: "This FY" },
        { key: "prev_fy", label: "Previous FY" },
        { key: "3m", label: "Last 3 M" },
        { key: "6m", label: "Last 6 M" },
        { key: "12m", label: "Last 12 M" },
        { key: "all", label: "All Data" },
    ];
    return (
        <>
            <div className="fixed inset-0 bg-[rgba(15,23,42,0.35)] z-40" onClick={onClose} data-testid="slicer-backdrop" />
            <div
                data-testid="slicer-drawer"
                className="fixed right-0 top-0 h-screen w-[320px] bg-[var(--bg-panel)] border-l border-panel z-50 shadow-lg overflow-y-auto pbi-card"
                style={{ borderRadius: 0 }}
            >
                <div className="p-4 border-b border-panel flex items-center justify-between sticky top-0 bg-[var(--bg-panel)] z-10">
                    <div className="flex items-center gap-2">
                        <Sliders size={16} className="text-[var(--pbi-blue)]" />
                        <div className="font-semibold text-sm">Slicers</div>
                    </div>
                    <button data-testid="btn-close-slicer" onClick={onClose} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
                        <X size={18} />
                    </button>
                </div>

                <SlicerSection title="Period">
                    <div className="grid grid-cols-2 gap-1.5">
                        {PERIODS.map((p) => (
                            <button
                                key={p.key}
                                data-testid={`slicer-period-${p.key}`}
                                onClick={() => setSlicer((s) => ({ ...s, period: p.key }))}
                                className={`px-2 py-1.5 border text-[11px] rounded transition-colors ${slicer.period === p.key ? "border-[var(--pbi-blue)] bg-[var(--pbi-blue-dim)] text-[var(--pbi-blue)] font-semibold" : "border-panel text-[var(--text-secondary)] hover:border-[var(--pbi-blue)]"}`}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                </SlicerSection>

                <SlicerSection title={`Projects (${slicer.project_ids.length} of ${options.projects.length})`}>
                    <MultiPicker
                        items={options.projects.map((p) => ({ id: p.id, label: p.name || p.id, sub: p.client_id || "" }))}
                        selected={slicer.project_ids}
                        onToggle={(id) => toggleId("project_ids", id)}
                        testIdPrefix="slicer-project"
                    />
                </SlicerSection>

                <SlicerSection title={`Clients (${slicer.client_ids.length} of ${options.clients.length})`}>
                    <MultiPicker
                        items={options.clients.map((c) => ({ id: c.id, label: c.name || c.id, sub: c.type || "" }))}
                        selected={slicer.client_ids}
                        onToggle={(id) => toggleId("client_ids", id)}
                        testIdPrefix="slicer-client"
                    />
                </SlicerSection>

                <SlicerSection title={`Bank Accounts (${slicer.account_ids.length} of ${options.accounts.length})`}>
                    <MultiPicker
                        items={options.accounts.map((a) => ({ id: a.id, label: a.bank_name || a.id, sub: a.account_no ? `A/C ${a.account_no}` : a.id }))}
                        selected={slicer.account_ids}
                        onToggle={(id) => toggleId("account_ids", id)}
                        testIdPrefix="slicer-account"
                    />
                </SlicerSection>

                <div className="p-4 border-t border-panel sticky bottom-0 bg-[var(--bg-panel)] flex gap-2">
                    <button
                        data-testid="btn-clear-slicer"
                        onClick={onClear}
                        className="flex-1 px-3 py-2 border border-panel rounded text-xs font-semibold text-[var(--text-secondary)] hover:border-[var(--neg)] hover:text-[var(--neg)]"
                    >
                        Clear All
                    </button>
                    <button
                        data-testid="btn-apply-slicer"
                        onClick={onClose}
                        className="flex-1 px-3 py-2 bg-[var(--pbi-blue)] text-white rounded text-xs font-semibold"
                    >
                        Apply
                    </button>
                </div>
            </div>
        </>
    );
}

function SlicerSection({ title, children }) {
    return (
        <div className="p-4 border-b border-panel">
            <div className="text-[10px] uppercase text-[var(--text-secondary)] tracking-wider mb-2 font-semibold">{title}</div>
            {children}
        </div>
    );
}

function MultiPicker({ items, selected, onToggle, testIdPrefix }) {
    const [q, setQ] = useState("");
    const filtered = q ? items.filter((i) => (i.label || "").toLowerCase().includes(q.toLowerCase())) : items;
    return (
        <div>
            <input
                data-testid={`${testIdPrefix}-search`}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search…"
                className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1 text-xs mb-2 rounded"
            />
            <div className="max-h-[180px] overflow-y-auto space-y-1">
                {filtered.slice(0, 30).map((it) => (
                    <label
                        key={it.id}
                        data-testid={`${testIdPrefix}-${it.id}`}
                        className={`flex items-start gap-2 py-1 px-1 text-[11px] cursor-pointer rounded hover:bg-[var(--bg-panel-hover)] ${selected.includes(it.id) ? "bg-[var(--pbi-blue-dim)]" : ""}`}
                    >
                        <input
                            type="checkbox"
                            checked={selected.includes(it.id)}
                            onChange={() => onToggle(it.id)}
                            className="mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                            <div className="truncate text-[var(--text-primary)]">{it.label}</div>
                            {it.sub && <div className="truncate text-[10px] text-[var(--text-muted)]">{it.sub}</div>}
                        </div>
                    </label>
                ))}
                {filtered.length === 0 && (
                    <div className="text-[10px] text-[var(--text-muted)] italic py-2">No matches</div>
                )}
                {filtered.length > 30 && (
                    <div className="text-[10px] text-[var(--text-muted)] italic py-2">+ {filtered.length - 30} more · refine your search</div>
                )}
            </div>
        </div>
    );
}

const SankeyOverviewNode = ({ x, y, width, height, index, payload }) => {
    if (!payload) return null;
    const isSource = payload.kind === "source";
    const isHub = payload.kind === "hub";
    const color = isHub ? "#fb8b1e" : isSource ? "#4af6c3" : "#c084fc";
    const labelX = isSource ? x - 6 : x + width + 6;
    const anchor = isSource ? "end" : "start";
    return (
        <Layer key={`node-${index}`}>
            <Rectangle x={x} y={y} width={width} height={height} fill={color} fillOpacity={0.85} />
            <text
                textAnchor={anchor}
                x={labelX}
                y={y + height / 2 + 4}
                fontSize="10"
                fill="#e5e7eb"
                fontFamily="'IBM Plex Mono', monospace"
            >
                {payload.name}
            </text>
        </Layer>
    );
};

function AnomalyRow({ icon: Icon, tone, label, count, value }) {
    const color = tone === "neg" ? "#ff433d" : tone === "warn" ? "#fb8b1e" : "#8892B0";
    return (
        <div className="flex items-center gap-2 py-1 border-b border-[var(--border)] last:border-0">
            <Icon size={12} style={{ color }} />
            <div className="flex-1 min-w-0">
                <div className="text-[11px] leading-tight">{label}</div>
                <div className="text-[10px] font-mono-fin text-[var(--text-secondary)] uppercase">{count} flag{count === 1 ? "" : "s"}</div>
            </div>
            <div className="text-[11px] font-mono-fin" style={{ color }}>{fmtINR(value)}</div>
        </div>
    );
}
